/**
 * use1RM — Estimated One-Rep Max Hook
 *
 * Implements the Epley formula:
 *   1RM = w * (1 + r / 30)
 *
 * Where:
 *   w = weight lifted (lbs)
 *   r = reps performed
 *
 * Provides:
 *   - calc1RM(weight, reps)          → single computation
 *   - useExercise1RM(exerciseId)     → reactive best 1RM from history
 *   - use1RMHistory(exerciseId)      → time-series for charting
 *   - calcProgressiveWeight(...)     → W_next from overload step function
 */

import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import type { RPE } from '@/types';

// ── Pure Math ────────────────────────────────────────────────

/**
 * Epley formula.
 * Returns 0 if reps === 1 (weight IS the 1RM).
 */
export function calc1RM(weightLbs: number, reps: number): number {
  if (reps <= 0 || weightLbs <= 0) return 0;
  if (reps === 1) return weightLbs;
  return weightLbs * (1 + reps / 30);
}

/**
 * Progressive Overload Step Function.
 * Calculates W_next based on the RPE of the previous session's last working set.
 *
 * Delta:
 *   RPE ≤ 6  → +5%
 *   RPE 7–8  → +2.5%
 *   RPE ≥ 9  → +0% (maintain)
 *
 * Rounds to nearest increment (default 2.5 lbs for dumbbells/isolation,
 * 5 lbs for barbells/compounds).
 */
export function calcProgressiveWeight(
  lastWeightLbs: number,
  lastRPE: RPE,
  roundTo: 2.5 | 5 = 2.5,
): number {
  if (lastWeightLbs <= 0) return 0;

  let delta = 0;
  if (lastRPE <= 6) delta = 0.05;
  else if (lastRPE <= 8) delta = 0.025;
  // RPE ≥ 9 → delta = 0 (maintain)

  const raw = lastWeightLbs * (1 + delta);
  return Math.round(raw / roundTo) * roundTo;
}

// ── Reactive Hooks ────────────────────────────────────────────

/** Returns the best estimated 1RM ever logged for an exercise. */
export function useExercise1RM(exerciseId: string): number {
  const best = useLiveQuery(async () => {
    const sets = await db.loggedSets
      .where('exerciseId')
      .equals(exerciseId)
      .filter((s) => !s.isWarmup)
      .toArray();

    if (sets.length === 0) return 0;

    return Math.max(...sets.map((s) => calc1RM(s.weightLbs, s.reps)));
  }, [exerciseId]);

  return best ?? 0;
}

/** Returns ISO-stamped 1RM series for a line chart (newest first). */
export interface OneRMDataPoint {
  date: string;   // ISO date string
  oneRM: number;  // lbs
}

export function use1RMHistory(exerciseId: string): OneRMDataPoint[] {
  const history = useLiveQuery(async () => {
    const sets = await db.loggedSets
      .where('exerciseId')
      .equals(exerciseId)
      .filter((s) => !s.isWarmup)
      .sortBy('loggedAt');

    // Group by calendar date, keep the max 1RM per day
    const byDate = new Map<string, number>();
    for (const s of sets) {
      const date = s.loggedAt.slice(0, 10); // YYYY-MM-DD
      const e1rm = calc1RM(s.weightLbs, s.reps);
      byDate.set(date, Math.max(byDate.get(date) ?? 0, e1rm));
    }

    return Array.from(byDate.entries())
      .map(([date, oneRM]) => ({ date, oneRM: Math.round(oneRM * 10) / 10 }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [exerciseId]);

  return history ?? [];
}

/**
 * Returns the suggested next weight for an exercise based on the most recent
 * working set logged for it. Returns null if no history exists.
 */
export function useSuggestedWeight(
  exerciseId: string,
  roundTo: 2.5 | 5 = 5,
): number | null {
  const suggestion = useLiveQuery(async () => {
    const lastSet = await db.loggedSets
      .where('exerciseId')
      .equals(exerciseId)
      .filter((s) => !s.isWarmup)
      .reverse()
      .limit(1)
      .first();

    if (!lastSet) return null;
    return calcProgressiveWeight(lastSet.weightLbs, lastSet.rpe, roundTo);
  }, [exerciseId, roundTo]);

  return suggestion ?? null;
}
